/**
 * Welcome screen shown on first run.
 *
 * Provides two options: create a new wallet or restore from a recovery phrase.
 * Friendly, approachable design matching Family/Revolut direction.
 *
 * Tier 1 (beginner) — first screen every user sees.
 *
 * @module WelcomeScreen
 */

import { Button } from '@/components/ui/button';

/** Props for WelcomeScreen component. */
interface WelcomeScreenProps {
  /** Called when user taps "Create New Wallet". */
  readonly onCreateWallet: () => void;
  /** Called when user taps "I Have a Recovery Phrase". */
  readonly onRestoreWallet: () => void;
}

/**
 * First-run welcome screen with create/restore wallet options.
 *
 * Tier 1 (beginner) — always shown to new users.
 */
export function WelcomeScreen({
  onCreateWallet,
  onRestoreWallet,
}: WelcomeScreenProps): React.ReactElement {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center px-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-8">
        {/* Logo / Brand area */}
        <div className="flex flex-col items-center gap-3">
          <div
            className="flex h-20 w-20 items-center justify-center rounded-3xl bg-primary"
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary-foreground"
            >
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Satsflow</h1>
          <p className="text-center text-base text-muted-foreground">
            Your Bitcoin wallet. Simple, private, yours.
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex w-full flex-col gap-3">
          <Button size="lg" className="w-full" onClick={onCreateWallet}>
            Create New Wallet
          </Button>
          <Button variant="outline" size="lg" className="w-full" onClick={onRestoreWallet}>
            I Have a Recovery Phrase
          </Button>
        </div>
      </div>
    </main>
  );
}

export type { WelcomeScreenProps };
